export const en = {
    seo: {
        title: 'MyCarCosts | Ultimate Car Expense Tracker & Repair Analysis',
        description: 'Track car maintenance, expenses, and history. Use our AI-powered repair vs sell analyzer to make data-driven decisions about your vehicle.'
    },
    common: {
        back: 'Back',
        save: 'Save',
        saved: 'Saved',
        cancel: 'Cancel',
        close: 'Close',
        accessDenied: 'Access Denied',
        noExportPermission: 'You do not have permission to export this car data.',
        delete: 'Delete',
        downgrade: 'Downgrade',
        downgradeConfirm: 'Are you sure you want to downgrade to Free plan?',
        edit: 'Edit',
        loading: 'Loading...',
        confirm: 'Confirm',
        processing: 'Processing...',
        history: 'History',
        currentCar: 'Current Car',
        purchasedCarTitle: 'Car Purchase',
        initialCost: 'Initial Cost',
        done: 'Done',
        ok: 'OK',
        error: 'Error',
        errorSaving: 'Error saving data',
        mandatoryFieldsError: 'Please fill in all mandatory fields correctly.',
        search: 'Search',
        milesShort: 'mi',
        kmShort: 'km',
        invalidEmail: 'Invalid email format',
        today: 'Today',
        car: 'Car',
        all: 'All',
        deleteConfirm: 'Delete',
        unsavedChangesTitle: 'Unsaved Changes',
        unsavedChangesMessage: 'Do you want to save changes before leaving?',
        dontSave: "Don't Save",
        additionalOptions: 'Additional options',
        add: 'Add',
        fileTooLarge: 'File is too large. Maximum size is {limit} MB.',
        storageFull: 'Storage limit exceeded. Maximum storage is {limit} MB.',
        dailyUploadLimit: 'Daily upload limit reached ({limit} files/day). Try again tomorrow.',
        hourlyUploadLimit: 'Hourly upload limit reached ({limit} files/hour). Try again in an hour.',
        fileCountLimit: 'Maximum {limit} files allowed per item.',
        emailParseLimitReached: 'Email parsing limit reached. Please upgrade your plan or process manually.',
        emailLimitText: 'Email processing limit reached.',
        monthlyEmailScans: 'Monthly Email Scans',
        lifetimeEmailScans: 'Lifetime Email Scans',
        monthlyFileScans: 'Monthly Receipt Scans',
        lifetimeFileScans: 'Lifetime Receipt Scans',
        fileLimitText: 'Receipt scan limit reached.',
        planFreeTier: 'Free tier',
        planProTier: 'Pro tier',
        uploadFailed: 'Upload failed',
        attachments: 'Attachments',
        limitsAndUsage: 'Limits & Usage',
        addresses: 'addresses',
        emailCopied: 'Email address copied!',
        units: {
            liters: 'l',
            gallons: 'gal',
            kilometers: 'km',
            miles: 'mi'
        },
        included: 'Included',
        proOnly: 'PRO only',
        month: 'mo',
        openInNewTab: 'Open in new tab',
        menu: 'Menu',
        home: 'Home',
        monthsShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    },
    carMetadata: {
        bodyType: 'Body Type',
        drivetrain: 'Drivetrain',
        transmission: 'Transmission',
        bodyTypes: {
            sedan: 'Sedan',
            hatchback: 'Hatchback',
            wagon: 'Wagon (Estate)',
            suv: 'SUV',
            crossover: 'Crossover',
            coupe: 'Coupe',
            convertible: 'Convertible',
            pickup: 'Pickup',
            van: 'Van / Minivan',
            commercial: 'Commercial',
            other: 'Other',
            not_specified: '-'
        },
        drivetrains: {
            not_specified: '-',
            fwd: 'FWD',
            rwd: 'RWD',
            awd: 'AWD / 4x4',
            other: 'Other'
        },
        transmissions: {
            not_specified: '-',
            manual: 'Manual',
            automatic: 'Automatic',
            cvt: 'CVT',
            other: 'Other'
        },
        otherLabel: 'Other (specify)',
        mileageLabel: 'Mileage'
    },
    auth: {
        welcome: 'Welcome',
        welcomeBack: 'Welcome Back',
        signIn: 'Sign In',
        signInWithGoogle: 'Sign in with Google',
        signOut: 'Sign Out',
        magicLinkDesc: 'Get a magic link in your email',
        magicLinkSent: 'Magic link sent!',
        magicLinkCheckEmail: 'Check your email and click the link to log in.',
        emailAddress: 'Email Address',
        getMagicLink: 'Get Magic Link',
        sending: 'Sending...',
        linkSent: 'Link Sent',
        orContinueWith: 'Or continue with',
        googleAccount: 'Google Account',
        newUserNote: 'New here? Your account will be created automatically upon your first successful login.',
        magicLinkError: 'Failed to send magic link. Please check your email and try again.',
        genericError: 'An error occurred. Please try again.',
        projectDescription: 'Track your car costs, maintenance, and history in one place.',
        loginError: 'Unable to sign in',
        loggingIn: 'Signing in...',
        authenticating: 'Authenticating...',
        authenticatingDesc: 'Please wait while we secure your session.',
    },
    settings: {
        title: 'Settings',
        systemPreferences: 'System Preferences',
        appSettings: 'App Settings',
        dangerZone: 'Danger Zone',
        theme: 'Theme',
        language: 'Interface Language',
        currency: 'Currency',
        units: 'Units',
        distanceUnit: 'Distance Unit',
        volumeUnit: 'Volume Unit',
        expenseTypes: 'Expense Types',
        expenseTypesDesc: 'Manage categories',
        categories: 'Categories',
        exportData: 'Export Data',
        exportDesc: 'Download comprehensive CSV',
        importData: 'Import Data',
        importDesc: 'Restore from CSV backup',
        deleteAccount: 'Delete Account',
        deleteAccountDesc: 'Permanently remove all data',
        enableSync: 'Enable Sync',
        enableSyncDesc: 'Sync data across devices',
        account: 'Account',
        accountPlan: 'Account Plan',
        carsUsage: 'Cars',
        carsLimitReached: 'Car limit reached',
        storageSize: 'Storage Size',
        emailScans: 'Email Scans',
        fileScans: 'Receipt Scans',
        plans: 'Plans',
        currentlyPlan: 'Currently',
        upgradePlan: 'Upgrade',
        manageSubscription: 'Manage Subscription',
        nextPayment: 'Next payment',
        endsOn: 'Ends on',
        adminConsole: 'Admin Console',
        systemControl: 'System Control',
        customCategories: 'Custom Categories & Labels',
        interfaceTheme: 'Interface Theme',
        light: 'Light',
        dark: 'Dark',
        timezone: 'Timezone',
        timezoneDesc: 'Used for expenses and scheduled email reminders',
        timeFormat: 'Time Format',
        timeFormat24h: '24-hour (14:30)',
        timeFormat12h: '12-hour (2:30 PM)',
        kilometers: 'km',
        miles: 'miles',
        liters: 'liters (l)',
        gallons: 'gallons (gal)',
        accountManagement: 'Account Management',
        contactUs: 'Contact us',
        contactDesc: 'For suggestions, feature requests and questions',
        logOut: 'Log Out',
        logOutConfirmTitle: 'Log Out',
        logOutConfirmMessage: 'Are you sure you want to sign out of your account?',
        keepAccount: 'Keep Account',
        deleteAccountConfirmTitle: 'Delete Account',
        deleteAccountConfirmMessage: 'Are you sure you want to permanently delete your account and all associated car data? This action cannot be undone.',
        deletePermanently: 'Delete Permanently',
        deleting: 'Deleting...',
        contactSupport: 'Contact Support',
        messageSent: 'Message Sent!',
        feedbackThanks: 'Thank you for your feedback. We appreciate your input and will look into it.',
        backToSettings: 'Back to Settings',
        subjectLabel: 'Subject',
        messageLabel: 'Message',
        subjectPlaceholder: 'What is this about?',
        messagePlaceholder: 'Tell us about your suggestion, feature request or question...',
        enterSubjectAlert: 'Please enter a subject',
        enterMessageAlert: 'Please enter a message',
        sendFailedAlert: 'Failed to send message. Please try again later.',
    },
    api: {
        title: 'API Settings',
        token: 'API Token',
        generateToken: 'Generate Token',
        rotateToken: 'Rotate Token',
        deleteToken: 'Delete Token',
        copyToken: 'Copy Token',
        tokenCopied: 'Token copied to clipboard',
        noTokenYet: "You haven't generated an API token yet.",
        rotateWarning: 'Rotating the token will immediately invalidate the old one. Any applications using it will stop working.',
        deleteWarning: 'Deleting the token will immediately invalidate it.',
        apiUsage: 'API Usage',
        dailyLimit: 'Daily Limit',
        hourlyLimit: 'Hourly Limit',
        endpoints: 'Available Endpoints',
        docsLink: 'View full API documentation',
        keepSafe: 'Keep this token safe. It has no expiration and gives full access to your car data.',
        tokenDesc: 'Use this token to interact with your garage data via the Public API or Jarvis integrations.',
    },
    export: {
        title: 'Export Data',
        generatePDF: 'Generate PDF',
        carInformation: 'Car Information',
        editInfo: 'Edit the information below to customize your export',
        editInfoHint: 'You can remove or modify any fields you don\'t want to include',
        filters: 'Filters',
        expenseTypes: 'Expense Types',
        includeNotes: 'Include Notes',
        includeImages: 'Include Files',
        hideDisabled: 'Hide Disabled Items',
        itemsCount: '{total} items, {selected} selected',
        selectAll: 'Select All',
        deselectAll: 'Deselect All',
        expenses: 'Expenses',
        notes: 'Notes',
        spareParts: 'Spare Parts',
        images: 'Images',
        files: 'Files',
        summary: 'Summary',
        totalExpenses: 'Total Expenses',
        totalItems: 'Total Items',
        generatedOn: 'Generated on',
        noItemsSelected: 'No items selected for export',
        noItemsAvailable: 'No items available to export',
        generating: 'Generating PDF...',
        downloadPDF: 'Download PDF',
        pdfFileName: '{carName}_export_{date}.pdf',
        reportTitle: 'Car Expense Report',
        exportButton: 'Export',
        generateCSV: 'Generate CSV',
        formatSelectorTitle: 'Choose Format',
        formatSelectorMsg: 'Please choose the export format',
        proFeatureExportTitle: 'Pro Feature: Export Data',
        proFeatureExportDesc: 'Upgrade to PRO to export your car history to PDF.',
        exportRestrictedPopup: 'Data export is only available for Pro users.',
        allTypes: 'All Types',
        statistics: 'Statistics',
        driven: 'Driven',
        since: 'Since',
        averageCost: 'Average Cost',
        perKm: 'per {unit}',
        perMile: 'per mile',
        period: 'Period',
        dateFrom: 'From',
        dateTo: 'To',
        allTime: 'Clear filter',
        periodHint: 'If no period is selected — all expenses are shown',
        noExpenses: 'No expenses match the filters',
        expensesSection: 'Expenses',
        attachmentsSection: 'Attachments',
        imagesCount: '{count} images',
        pdfsCount: '{count} PDFs',
        includeParts: 'Include spare parts',
        pageLabel: 'Page',
    },
    expenses: {
        title: 'Expenses',
        newExpense: 'New Expense',
        editExpense: 'Edit Expense',
        openExpense: 'Open Expense entry',
        details: 'Expense Details',
        selectType: 'Select Type',
        selectTypePlaceholder: 'Select expense type...',
        shortDescriptionPlaceholder: 'Short description...',
        expenseTitle: 'Title',
        logEntryTitleLabel: 'Record Title',
        defaultTitlePlaceholder: 'Default title - {type}',
        autoTitlePlaceholder: 'Will be auto-filled based on selected types',
        carDataTitle: 'Car Data',
        defaultTitlePrefix: 'Default title will be -',
        addAnother: 'Add another type of expense',
        addAnotherHint: 'If this record includes multiple types of expenses, you can add them all here to ensure accurate statistics.',
        carValueDescription: 'Estimated market value of the car at this time.',
        type: 'Type',
        date: 'Date',
        amount: 'Amount',
        totalAmount: 'Total',
        splitCosts: 'Split costs?',
        combineCosts: 'Combine costs',
        amountWork: 'Work',
        amountParts: 'Parts',
        mileage: 'Mileage',
        carValue: 'Car Value',
        description: 'Description',
        detailedDescription: 'Detailed description',
        electricity: 'Electricity',
        fuelType: 'Fuel Type',
        fuelTypeOptions: {
            petrol: 'Petrol',
            diesel: 'Diesel',
            lpg: 'LPG',
            electricity: 'Electricity'
        },
        carData: 'Car Data',
        images: 'Files',
        addImages: 'Add Files',
        addImagesPDFs: 'Add Images or PDFs',
        deleteExpense: 'Delete Expense',
        deleteConfirm: 'Are you sure you want to delete this expense?',
        saveSuccess: 'Expense saved successfully',
        thisExpense: 'This expense',
        stats: {
            annual: 'Annual',
            allTime: 'All Time',
            avgMonth: 'Avg/Month',
            costPer: 'Cost/',
            noExpenses: 'No expenses yet',
            dragToReorder: 'Drag handle to reorder'
        },
        expensesFound: 'Created',
        noExpensesFound: 'No expenses found.',
        noDescription: 'No description',
        imagesCount: 'Images ({count})',
        deleteImageTitle: 'Delete Image',
        deleteImageConfirm: 'Are you sure you want to delete this image? This action cannot be undone.',
        typeLabels: {
            fuel: 'Fuel',
            car_wash: 'Washing',
            repairs: 'Repairs',
            maintenance: 'Maintenance',
            parts: 'Spare Parts',
            insurance: 'Insurance',
            inspection: 'Inspection',
            tyres: 'Tires',
            parking: 'Parking',
            electricity: 'Electricity',
            taxes: 'Taxes',
            other: 'Other',
            info: 'Information',
            service_maintenance: 'Maintenance',
            service_repair: 'Repair',
            service_tuning: 'Tuning',
            tolls: 'Tolls',
        },
        serviceGroup: {
            name: 'Service',
            selectSubtype: 'Select service type',
            maintenance: 'Maintenance',
            repair: 'Repair',
            tuning: 'Tuning',
        },
        timeLabel: 'Time',
        addTimeLabel: 'Add time',
        removeTimeLabel: 'Remove time',
    },
    repeatingExpenses: {
        title: 'Repeating Expense',
        repeatToggle: 'Repeat this expense',
        repeatFrequency: 'Frequency',
        monthly: 'This expense will be re-created monthly.',
        startDate: 'Start Date',
        nextDate: 'Next entry will be generated',
        endDate: 'End Date',
        noEndDate: 'No end date',
        copyOptions: 'When repeating this expense:',
        copyTitle: 'Copy also record title',
        copyLocation: 'Copy also location',
        copyDescription: 'Copy also description',
        active: 'Active',
        status: 'Status',
        managedByRepeat: 'This expense is part of a repeating sequence.',
        placeholder: 'Here you can set up automatic repetition for this expense if it occurs regularly with the same amount.',
        deleteRepeat: 'Delete Repetition',
        deleteRepeatConfirm: 'Are you sure you want to stop and delete this repeating sequence? Future entries will not be generated. Past entries will remain.',
        relatedEntries: 'Entries in this sequence',
        stopRepeat: 'Stop repeating',
        isRepeating: 'Repeating',
        noTerm: 'Indefinitely',
        until: 'Last entry will be generated',
        summaryCopy: 'System will copy',
        simulate: 'Simulate next entry generation',
        simulateSuccess: 'Successfully simulated next entry generation',
        simulateNoDate: 'No more dates to generate (end date reached)',
    },
    onboarding: {
        title: 'Setup Profile',
        welcome: 'Welcome!',
        subtitle: 'Customize your driving experience.',
        currency: 'Currency',
        preferredCurrency: 'Preferred Currency',
        distUnit: 'Distance Unit',
        volUnit: 'Volume Unit',
        complete: 'Complete Setup',
    },
    cars: {
            unitChangeWarningTitle: 'Unit Change Warning',
            unitChangeWarningMessage: 'Changing these units will not convert your existing data. All historical records will remain exactly as entered.',
            unitChangeWarningConfirm: 'Understood',
        title: 'Garage',
        carAdmins: 'Car Administrators',
        sharedWithMe: 'Shared with Me',
        manageAdmins: 'Manage Administrators',
        adminEmail: 'Administrator Email',
        addAdmin: 'Add Administrator',
        addAdminPlaceholder: 'e.g. friend@email.com',
        removeAdmin: 'Remove',
        removeAdminConfirm: 'Are you sure you want to remove this administrator?',
        adminAdded: 'Administrator added',
        adminRemoved: 'Administrator removed',
        removeMyAccess: 'Remove my access',
        removeMyAccessConfirm: 'Are you sure you want to give up access to this car?',
        myAccessRemoved: 'Access removed',
        noAdminsYet: 'No administrators added yet.',
        adminInfoText: 'Administrators can add and edit expenses, notes, and spare parts, but cannot delete the car itself.',
        newCar: 'Add Car',
        carNotFound: 'Car Not Found',
        carNotFoundDesc: "The requested car does not exist or you don't have access.",
        accessDenied: 'Access Denied',
        accessDeniedDesc: 'This car is private and not publicly available.',
        backToHome: 'Back to Home',
        adminRestricted: 'Access Restricted',
        adminRestrictedDesc: 'Only authorized administrators can access this console. Please return to the main application.',
        returnHome: 'Return Home',
        editCar: 'Edit Car',
        name: 'Car Name',
        model: 'Model',
        year: 'Year',
        plate: 'Plate Number',
        vin: 'VIN Number',
        mileage: 'Current Mileage',
        initialMileage: 'Initial Mileage',
        engineType: 'Engine Type',
        tapToChangeIcon: 'Tap to change icon',
        chooseIcon: 'Choose Icon',
        imageTab: 'Image',
        iconTab: 'Icon',
        chooseColor: 'Choose Color',
        chooseEmoji: 'Choose Emoji',
        uploadImage: 'Upload Image',
        clickToBrowse: 'Click to browse files',
        currentCarIcon: 'Current car icon',
        changeImage: 'Change Image',
        zoom: 'Zoom',
        rotate: 'Rotate',
        removeChangeImage: 'Remove / Change Image',
        selectIcon: 'Select Icon',
        cropAndSave: 'Crop & Save',
        displayName: 'Display Name',
        displayNamePlaceholder: 'e.g. My Weekend Car',
        makeLabel: 'Make *',
        modelLabel: 'Model *',
        yearLabel: 'Year *',
        fuelTypeLabel: 'Fuel Type *',
        licensePlateLabel: 'License Plate',
        vinLabel: 'VIN',
        notesLabel: 'Notes',
        notesPlaceholder: 'e.g. 1.6 TDI, 85kW, 1588cc, battery 62kWh, AWD, etc.',
        publicAccess: 'Make car publicly accessible',
        publicAccessDesc: 'Others can view this car via shared link',
        shareLocations: 'Allow to view also added locations',
        shareLocationsDesc: 'Enable this to make car locations visible in public view',
        deleteCar: 'Delete Car',
        deleteCarTitle: 'Delete Car',
        deleteCarConfirm: 'Are you sure you want to delete "{name}"? This action cannot be undone.',
        noCars: 'No cars in your garage',
        addFirstCarNote: 'Start your journey by adding your first vehicle to track expenses.',
        addVehicle: 'Add A Vehicle',
        myVehicles: 'My Vehicles',
        favorites: 'Favorites',
        deleteConfirm: 'Are you sure you want to delete this car? All associated expenses will be deleted.',
        deleteFailed: 'Failed to delete car',
        saveFailed: 'Failed to save car data',
        adminActions: 'Admin Actions',
        importCsv: 'Import CSV',
        clearData: 'Clear Data',
        distanceUnitLabel: 'Distance Unit',
        volumeUnitLabel: 'Volume Unit',
        currencyLabel: 'Currency',
        boughtAt: 'Purchase Date',
        purchasePrice: 'Purchase Price',
        purchaseMileage: 'Purchase Mileage',
        createPurchaseRecord: 'Create purchase history record automatically',
        makePlaceholder: 'e.g. BMW',
        modelPlaceholder: 'e.g. X5',
        platePlaceholder: 'e.g. AB-1234',
        vinPlaceholder: 'VIN...',
        purchaseInfoNote: 'This information will create an initial "Information" record in your history with the specified data.',
        availableTypes: 'Available Types',
        dragToReorder: 'Drag to reorder',
        requestTypeNote: 'If you need another type - ',
        requestTypeAction: 'let us know!',
        emailProcessingTitle: 'Email Processing',
        emailInstructions: 'Forward your receipts to the address below. We\'ll extract data and create expenses automatically.',
        recentEmails: 'Received Emails',
        viewAllEmails: 'VIEW ALL ({count})',
        noEmailsReceived: 'No emails received yet.',
        analysisResult: 'Analysis Result',
        vehicleInfo: 'Vehicle Information',
        preferences: 'Preferences',
        purchaseHistory: 'Purchase History',
        emailProcessing: 'Email Processing',
        publicAccessTitle: 'Public Access',
        expenseTypesTitle: 'Expense Types',
        adminActionsTitle: 'Admin Actions',
        enterPurchaseDateAlert: 'Please enter purchase date',
        allowedSenderEmails: 'Allowed Sender Emails',
        allowedEmailsDesc: 'Add email addresses that are allowed to send receipts to this car. Maximum 5 addresses.',
        noAllowedEmails: 'No allowed emails added.',
        emailAlreadyAdded: 'This email is already allowed for this car.',
        maxAllowedEmailsReached: 'Maximum limit of allowed emails reached.',
        addAllowedEmailPlaceholder: 'Add allowed email...',
        ownerEmail: 'Owner',
        onlyOwner: 'owner only',
        defaultAllowed: 'Always allowed',
        carLimitReachedTitle: 'Car Limit Reached',
        carLimitReachedMessage: 'Your {plan} plan allows {limit} car(s). You already have {current}. Go to settings to see your limits or contact us to upgrade.',
        carLimitGoToSettings: 'Go to Settings',
        archivedCars: 'Archived',
        archiveCar: 'Archive Car',
        unarchiveCar: 'Unarchive Car',
        sellDate: 'Sell Date',
        sellPrice: 'Sell Price',
        sellMileage: 'Sell Mileage',
        archiveConfirm: 'Are you sure you want to archive this car?',
        archiveInstructions: 'Archiving a car means it is no longer yours or you no longer maintain it. Please provide the sale details for statistics. This will make the car read-only. The fields below can still be edited afterwards.',
        archivedBadge: 'Archived',
        saleDetails: 'Sale Details',
        saleDetailsNote: 'Sale details are used for statistics. You can update them if needed.',
        archiveDetailsNotFillable: 'Please fill all fields to archive the car',
        emailAddressWillBeAvailableAfterSave: 'Email address will be available after saving the car.',
        proFeatureAdminTitle: "Pro feature: Car administrators",
        proFeatureAdminDesc: "Add other users who can view and manage this car data.",
        proFeatureEmailTitle: "Pro feature: Extra senders",
        proFeatureEmailDesc: "Add other allowed email addresses from which receipts can be sent to this car.",
        estimatedMileage: "Estimated mileage",
        summaryFrequencyLabel: "Summary Frequency",
        summaryFrequencyOptions: {
            none: "Off",
            monthly: "Monthly",
            semiAnnually: "Semi-Annually",
            annually: "Annually"
        },
        healthProfilePrompt: "Complete your health profile for accurate analysis!",
    },
    fuelTypes: {
        not_specified: '-',
        petrol: 'Petrol',
        diesel: 'Diesel',
        hybrid: 'Hybrid',
        electric: 'Electric',
        lpg: 'LPG',
    },
    statistics: {
        mileageTrend: "Mileage Trend",
        title: 'Statistics',
        allYears: 'All years',
        totalSpent: 'Total Spent',
        spentIn: 'Spent in {year}',
        perMonth: 'per month',
        overall: 'overall',
        vsPrev12m: 'vs prev 12m',
        rolling12m: 'Rolling 12m',
        byType: 'By Type',
        byMonth: 'By Month',
        byYear: 'By Year',
        noData: 'No data for this year',
        mostExpensive: 'Most expensive',
        top: 'Top',
        untitled: 'Untitled',
        assetAnalysis: 'Asset Value & Cost Analysis',
        burnNote: '* The "Total Burn" includes both money spent and the estimated decrease in car value.',
        carMarketValue: 'Car Market Value',
        cumulativeCosts: 'Cumulative Costs',
        totalBurn: 'Total Burn (Costs + Depr)',
        entries: 'entries',
        avgPrice: 'Avg Price',
        consumption: 'Consumption',
        price: 'Price',
        fuelPriceTrend: 'Fuel Price Trend',
        spendingTrend: 'Spending Trend',
        costPerKmTrend: 'Cost per 1 {unit}',
        costPerMileTrend: 'Cost per 1 mile',
        serviceTrendsTitle: 'Service Trends',
        expensesOnMap: 'Expenses on Map',
        visitsCountLabel: 'visits',
        locationsSummary: 'Locations Summary',
        locationsCountLabel: 'places',
        noLocationsFoundTitle: 'No locations yet',
        noLocationsFoundDesc: 'Add locations to your records to see them on the map.',
        addMileageData: 'Add mileage',
        missingMileageInfo: 'Not enough data to calculate cost per distance. Add at least 2 mileage readings in this period (or one before this period).',
        mileageModal: {
            title: 'Add Missing Mileage',
            description: 'To accurately calculate costs per distance for {year}, we need to know the mileage at the start and end of the year.',
            startOfYear: 'Start of year (Jan 1st)',
            endOfYear: 'End of year (Dec 31st)',
            currentMileage: 'Current mileage',
            placeholder: 'Enter mileage...',
            save: 'Save Data',
            foundNearby: 'Found nearby ({date}): {value} {unit}',
            useExisting: 'Use this',
        },
        proAnalysis: "Pro Analysis",
        detailedAnalysis: "Detailed Analysis",
        upgradeToSee: "Upgrade to see →",
    },
    blog: {
        latestPosts: 'Latest from the Blog',
        morePosts: 'View all articles',
        readMore: 'Read more',
        readArticle: 'Read Article',
        back: 'Back',
        backToBlog: 'Back to Blog',
        share: 'Share this story:',
        checkMath: 'Check your own math',
        resourceCenter: 'Resource Center',
        noMorePosts: 'No more posts yet. More coming soon!',
        keepReading: 'Keep Reading',
        title: 'Resource Center',
        subtitle: 'We are not just building another spreadsheet. We are building a resource center to help you make better financial decisions.'
    },
    tabs: {
        dashboard: 'Dashboard',
        expenses: 'Expenses',
        stats: 'Statistics',
        locations: 'Locations',
        gallery: 'Files',
        parts: 'Parts',
        notes: 'Notes',
        repair: 'Car Health',
        'repair-switch': 'Car Switch',
    },
    repairAnalysis: {
        title: 'Repair Decision Analysis',
        ownershipLength: 'Ownership Length',
        currentValue: 'Current Value',
        valueOutdated: 'Value may be outdated',
        addValueToEnable: 'Add current car value to enable analysis',
        totalInvested: 'Total Invested',
        totalInvestedSub: 'Purchase + all repairs *',
        totalExcludesFuel: 'excludes fuel / electricity',
        fuelLabelFuelOnly: 'Fuel total',
        fuelLabelEnergyOnly: 'Energy total',
        fuelLabelHybrid: 'Fuel & Energy total',
        fuelTypeFuel: 'fuel',
        fuelTypeEnergy: 'electricity',
        fuelTypeHybrid: 'fuel & electricity',
        fuelTotalLabel: 'Fuel/Energy total:',
        projectionMode: 'Projection Mode',
        projectionModes: {
            lastYear: 'Based on last year',
            average: 'Historical average',
            trend: 'Trending (linear)'
        },
        brakeAssumption: 'Maintenance includes estimated 3yr brake cycle',
        repairBuffer: 'Includes age-based repair risk buffer',
        yearLabel: 'year',
        totalInvestedDesc: 'Purchase price + all repairs',
        repairToValue: 'Repair to Value (12m)',
        repairToValueExplain: 'Repairs €{cost} vs car value €{value}. Under 20% is healthy; above 50% is high risk.',
        ownershipPressure: 'Ownership Pressure',
        ownershipPressureExplain: 'Total cost pressure: repairs €{cost} + value loss vs €{value}. Above 35% signals financial stress.',
        investmentGap: 'Investment Gap',
        investmentGapExplain: 'Total invested €{invested} minus current value €{value}. This shows how much you have \'lost\' on ownership so far.',
        investmentGapDesc: 'Total invested - current value',
        repairExpenses12m: '12m Repair Cost',
        calculatedYearlyMileage: 'Calculated yearly mileage: {km} {unit}',
        calculatedYearlyRepairs: 'Calculated yearly repairs: {amount}',
        set: 'Set',
        plannedRepairCost: 'Planned repair costs',
        whatIfTitle: 'What if you spend {amount} now?',
        updatedScore: 'Updated score',
        updatedVerdict: 'Updated verdict',
        howIsItCalculated: 'How is this calculated?',
        usedLifespan: "Used: {usage}% of expected lifespan",
        remainingLifespan: "Remaining: ~{km} {unit}",
        estimatedLifespan: "Estimated lifespan: {km} {unit}",
        lifecycle_stage_early: "Early lifecycle – low risk of major failures",
        lifecycle_stage_mid: "Mid lifecycle – normal wear",
        lifecycle_stage_late: "Late lifecycle – increased risk of repairs",
        lifecycle_stage_end: "End of expected lifespan – high risk of major repairs",
        calculationExplanation: 'The analysis uses your vehicle\'s purchase price, recent repair costs, and estimated current value to calculate a financial health score. High repair costs relative to the car\'s value or rapid depreciation will lower the score.',
        confidenceLabel: 'Confidence',
        confidenceLevel: {
            high: 'High',
            medium: 'Medium',
            low: 'Low'
        },
        mentalFactorsTitle: 'Improve accuracy with your experience',
        mentalFactorsCTA: 'Quick check (30 sec)',
        mentalFactorsSurveyTitle: 'Your Experience',
        mentalFactorsSurveyDesc: 'Answer 3 quick questions to personalize your score.',
        q1: 'Does your car meet your needs?',
        q1Help: 'Space, comfort, features',
        q2: 'Do you enjoy driving your car?',
        q2Help: 'Visual, comfort, feeling',
        q3: 'Do you worry about unexpected breakdowns?',
        q3Help: 'Reliability concerns',
        q4: 'Is this car for family and long trips?',
        q4Help: 'Safety and reliability priority',
        factorsTitle: 'Score Factors',
        factorsDesc: 'See what impacts your score',
        newCarComparison: 'New Car Comparison',
        newCarComparisonDesc: 'Compare your current monthly cost to buying a new car.',
        modeLeasing: 'Leasing',
        modeFullPrice: 'Full Price',
        downPayment: 'Down Payment',
        leaseYears: 'Term (Years)',
        monthlyPayment: 'Monthly Payment',
        carPrice: 'Car Full Price',
        ownershipYears: 'Planned Ownership (Years)',
        insuranceMonthly: 'Monthly CASCO / Insurance',
        currentMonthlyCost: 'Current Auto Cost',
        newCarMonthlyCost: 'New Car Cost',
        newCarCheaper: 'New car is cheaper by: {amount} / mo',
        newCarMoreExpensive: 'New car is more expensive by: {amount} / mo',
        newCarSameCost: 'Costs are exactly the same.',
        advancedSettings: 'Advanced Settings',
        yearlyMaint: 'Yearly Maint. (€)',
        tyres3Yrs: 'Tyres / 3yrs (€)',
        depreciation5Yrs: 'Depreciation (5 yrs %)',
        newCarYear: 'New Car Year',
        cumulativeCosts: 'Cumulative Costs (Projection)',
        comparisonByYear: 'Comparison by Year',
        currentCarLabel: 'Current Car',
        newCarLabel: 'New Car',
        resaleValue: 'Resale Value',
        startupCosts: 'Startup Costs (Used Car)',
        inspection: 'Pre-purchase inspection',
        initialMaintenance: 'Initial maintenance + oils',
        registration: 'Registration / Fees',
        total: 'Total',
        currentCarTitle: 'Current Car',
        newCarTitle: 'Potential Car',
        upfrontInvestment: 'Upfront Investment',
        monthlyReality: 'Real Monthly Cost',
        depreciationShort: 'Depreciation',
        howCalculated: 'How is this calculated?',
        deltaMonthly: 'Monthly difference',
        deltaUpfront: 'Upfront difference',
        surveyMeanings: {
            practical: {
                1: 'Not at all',
                2: 'Poor fit',
                3: 'Average',
                4: 'Good fit',
                5: 'Perfect fit'
            },
            joy: {
                1: 'Frustrating',
                2: 'Boring',
                3: 'Average',
                4: 'Enjoyable',
                5: 'Very much'
            },
            worry: {
                1: 'Not worried',
                2: 'Slightly worried',
                3: 'Worried',
                4: 'Very worried',
                5: 'Sure I\'ll break down any moment'
            }
        },
        verdicts: {
            KEEP: 'Financially reasonable to keep',
            borderline: 'Borderline – monitor closely',
            sell: 'High financial risk – selling may be rational'
        },
        factors: {
            // These UI labels are for special composite/derived factors not in main config
            trendInc: 'Increasing Trend',
            trendIncDesc: 'Repair costs are growing compared to last year.',
            lowDepreciation: 'Low Depreciation Advantage',
            lowDepreciationDesc: 'Older/cheaper cars lose less value over time.',
            stableRepairs: 'Stable Repairs',
            stableRepairsDesc: 'Consistency in repair costs is a reliability indicator.',
            noHistoricalData: 'No data older than 6 months.',
            lifecycle_stage: "Vehicle lifecycle stage",
            high_risk_phase: "High-risk phase",
            high_risk_phase_desc: "The combination of a late lifecycle stage and increasing repair costs indicates high risk.",
            lowCostKm: 'Efficient Cost per KM',
            lowCostKmDesc: 'Overall efficiency of running this car is excellent.',
            lowFixedCosts: 'Low Fixed Costs',
            lowFixedCostsDesc: 'Your fixed costs (taxes, insurance) are low.',
            usage: 'Yearly Usage',
            usageDesc: 'Distance driven in the past 12 months.',
            accidentMinor: 'Minor Accident',
            accidentMinorDesc: 'Cosmetic damage or small repairs.',
            structuralDamage: 'Structural Damage',
            structuralDamageDesc: 'Frame or structural problems drastically lower the value.',
            aiLifespanNearEnd: 'End of Expected Lifespan',
            aiLifespanAging: 'Aging Components',
            aiLifespanGood: 'Low Mileage Advantage',
            aiLifespanDesc: 'Based on AI estimated typical lifespan for this model.',
            aiUpcomingRepairs: 'Upcoming Repairs (AI Predict)',
            aiUpcomingRepairsDesc: 'AI predicts major component failures soon based on mileage.',
            ownershipFatigue: 'Ownership Fatigue',
            ownershipFatigueLong: 'Long-term ownership often leads to mental readiness for a different vehicle.',
            ownershipFatigueShort: 'Initial ownership bias creates a stronger tendency to keep the car.',
            family_priority: 'Family & Long Trips',
            replacement_comparison: 'Replacement Comparison',
            // Severity value labels (used by CarHealthView to display localized status)
            none: 'None',
            surface: 'Surface',
            minor: 'Minor',
            moderate: 'Moderate',
            severe: 'Severe',
            structural: 'Structural',
            major: 'Major',
            critical: 'Critical',
            full: 'Full',
            // Improvement advice label
            improveLabel: 'What would improve this:',
            // Info tooltip texts (used by Diagnosis Centre sliders)
            info_original_price: 'The approximate price of this car when it was new. Helps estimate value stability. Older cars with lower remaining value are safer financially.',
            info_current_value: 'How much you could sell it for today. Used to check if repairs are a large percentage of car value, which signal bad investment.',
            info_yearly_mileage: 'Higher mileage increases wear and tear. Low mileage can significantly extend car life.',
            info_estimated_repairs: 'Ongoing cost of keeping the car running. High yearly costs reduce attractiveness of keeping the car.',
            info_planned_repairs: 'Known upcoming repairs. Simulates immediate pressure on your decision.',
            info_ownership_years: 'How long have you owned this car? This affects psychological ownership fatigue and initial buyer bias.',
            info_rust: 'Critical long-term risk. Structural rust reduces safety. Severe rust makes selling a better option.',
            info_crashes: 'Structural accidents affect safety and reliability. They also reduce resale value significantly.',
            info_repaint: 'Signals past damage or cosmetic fixes. Small impact unless deeper issues were hidden.',
            info_unresolved: 'Hidden future costs. Known faults in electronics, engine or maintenance that need attention.',
            info_fit: 'Does the car fit your needs (space, comfort)? A poor fit means you are more likely to replace it.',
            info_joy: 'Emotional value matters. No joy means you are mentally ready for a new car.',
            info_worry: 'Perceived reliability stress. High worry is a strong signal to replace the car.',
        },
        explanations: {
            repairFreq: "Your car had {visits} service visits in the last 12 months. Frequent visits indicate lower reliability and higher hassle.",
            repairVsValue: "In the last 12 months, your repairs cost €{cost}, while the car's estimated value is €{value}. That's {ratio}% of the car's total value.",
            stableRepairsDetailYearly: 'Last year you spent €{cost1} in repairs, but the year before €{cost2}. Change of {percent}% ({trend}).',
            stableRepairsDetailHalfYearly: 'Last 6 months you spent €{cost1}, previous 6 months €{cost2}. Change of {percent}% ({trend}).',
            mileage: "At {mileage} {unit}, the car enters a higher risk zone for major part failures (transmission, engine components).",
            planned: "You have €{cost} in planned repairs, which immediately reduces the financial safety of keeping the car.",
            lowDep: "Cars valued around €{value} lose very little value per year, counter-balancing higher repair costs.",
            depRisk: "Your car is {age} years old and has mostly completed its depreciation cycle, meaning you don't lose money on its value dropping.",
            stable: "You spent €{costThisYear} this year vs €{costLastYear} last year, which is a {percent}% decrease. Predictable costs are mathematically favorable.",
            costKm: "You drove {km} {unit} last year. Running costs were €{costKm} per kilometer, which shows excellent economic efficiency.",
            fixedCosts: "You paid €{costs} in fixed yearly costs (insurance/tax). This low footprint gives extra peace of mind.",
            usage: "You drive around {km} {unit} per year based on your logs.",
            practical_fit: "You evaluated the practical fit at {score}/5, adjusting the mathematical score by {impact} points.",
            emotional_satisfaction: "You evaluated your emotional satisfaction at {score}/5, adjusting the score by {impact} points.",
            reliability_anxiety: "Your reliability confidence is {score}/5, impacting the score by {impact} points.",
            rust: "Severe structural or underbody rust typically means safety hazards or write-off scenarios.",
            accident: "Previous damage, depending on severity, inherently reduces the vehicle's resale value and safety.",
            structural: "Structural problems automatically render the car financially dangerous.",
            ai_lifespan: "At {current} {unit}, the car has completed {ratio}% of its AI estimated lifespan ({expect} {unit}).",
            ai_repairs: "The AI data indicates {count} typical model-specific major repairs tend to happen at this mileage.",
            lifecycle_stage: "What does lifespan mean? {total} {unit} – this is the AI-predicted point when major structural repairs (engine, gearbox, etc.) typically begin for this model. It is not the end of the car's life – the car can still drive, but with significantly higher maintenance costs. A typical car's full physical lifespan is 500k–1M {unit}, but it becomes economically unviable much earlier."
        },
        ratios: {
            healthy: 'Healthy',
            monitor: 'Monitor',
            highRisk: 'High risk',
            stable: 'Stable',
            warning: 'Warning',
            financialStress: 'Financial stress'
        },
        proFeatureTitle: 'Pro Feature: Repair Mode',
        proFeatureDesc: 'Unlock the decision engine to get a professional financial analysis of your vehicle repairs. Free users see a demonstration with sample data.',
        viewDemo: 'View Live Demo',
        upgradeToUnlock: 'Upgrade to PRO to analyze your car'
    },
    carTasks: {
        title: "Car Tasks",
        placeholderTitle: "Maintenance Tracking",
        placeholderDesc: "Maintenance schedule for your car model.",
        demoTitle: "Car Maintenance Tasks",
        demoSubtitle: "Smart tracking for your vehicle",
        feature1Title: "Automated Task Generation",
        feature1Desc: "Automatically generates tasks like oil changes, filter replacements, and timing belt checks based on your car's model.",
        feature2Title: "Expense Integration",
        feature2Desc: "Analyzes your service history to understand what's already been done and when.",
        feature3Title: "Smart Reminders",
        feature3Desc: "Get notified based on both time and mileage. Tracking resets automatically when you mark a task as done.",
        getStarted: "Get Started",
        fakeDoorPopupTitle: "Feedback Welcome",
        fakeDoorPopupMessage: "We are currently developing car maintenance tasks. This feature will allow you to track service intervals automatically. What specific tasks or features would you like to see most?",
        fakeDoorPopupPlaceholder: "Your thoughts...",
        noTasksYet: "No tasks yet",
        deleteAllTitle: "Clear task list?",
        deleteAllMessage: "Are you sure you want to delete all {count} tasks for this car? This action cannot be undone.",
        deleteAllSuccess: "All tasks have been successfully deleted.",
        sortPriority: "Priority",
        sortType: "Type",
        sortDate: "Latest",
        status: {
            overdue: "OVERDUE",
            pending: "PENDING",
            noData: "NO DATA",
            upcoming: "Upcoming",
            nextIn: "Next in {dist} {unit}",
            overdueBy: "{dist} {unit} overdue",
            afterDays: "In {days} days",
            overdueDays: "{days} days overdue"
        },
        form: {
            newTitle: "New Task",
            actionType: "Inspect or Replace?",
            intervalMonths: "Interval (Months)",
            intervalDistance: "Interval (Distance)",
            diffFirstInterval: "First interval differs from base interval",
            firstInterval: "First Interval",
            historyToggle: "Has this task ever been performed for this car?",
            historyToggleDesc: "If NO, we assume it's never been done since production.",
            lastPerformedOptional: "When was it last performed? (Optional)",
            deleteTitle: "Delete Task",
            deleteConfirm: "Are you sure you want to delete this task?",
            saveError: "Failed to save task",
            historyConfirmed: "History Confirmed",
            reminders: "Reminders",
            notifyMileage: "Notify me when estimated mileage is reached",
            notifyDate: "Notify me when date is reached",
            nextMileageReminder: "Next target",
            nextDateReminder: "Planned date",
            estimatedDate: "Estimated date",
            missingLastMileage: "Missing last performed mileage",
            missingLastDate: "Missing last performed date",
            missingStartDate: "Could not determine start date",
            additionalOptions: "Additional Options",
            reminderInfoMissing: "To enable reminders, please toggle 'Has this task ever been performed?' and provide the last maintenance info."
        }
    },
    dashboard: {
        title: 'Dashboard',
        quickActions: 'Quick Actions',
        addExpense: 'Add Expense',
        editCar: 'Edit Car',
        recentExpenses: 'Expenses',
        seeAll: 'See All',
        recentGallery: 'Files',
        thisMonth: 'This Month',
        totalSpent: 'Total Spent',
        avgPerMonth: 'Avg/Month',
        recentNotes: 'Notes',
        recentParts: 'Parts',
        noRecentExpenses: 'No recent expenses',
        noRecentGallery: 'No recent photos',
        noRecentNotes: 'No recent notes',
        noRecentParts: 'No recent parts',
        createNote: 'Create New',
        createPart: 'Create New',
        createExpense: 'Create New',
        noFiles: 'No files added yet',
        needSomethingMore: 'Need something more?',
        letUsKnow: 'let us know!',
    },
    expenseTypes: {
        editTitle: 'Edit Expense Type',
        newTitle: 'New Expense Type',
        categoryName: 'Category Name',
        placeholderName: 'e.g. Service',
        signatureColor: 'Signature Color',
        icon: 'Category Icon',
        deleteConfirm: 'Are you sure you want to delete type "{name}"?',
    },
    spareParts: {
        title: 'Spare Parts',
        connectedWithRecords: 'Connected with Records',
        toBuy: 'To Buy',
        usedInExpenses: 'Used in Expenses',
        noPartsToBuy: 'No parts added to shopping list',
        noPartsUsed: 'No parts linked to expenses yet',
        quantity: 'Quantity',
        partCode: 'Part Code',
        price: 'Price',
        link: 'Link',
        comment: 'Comment',
        addPart: 'Add Spare Part',
        editPart: 'Edit Spare Part',
        openPart: 'Open Part entry',
        nameLabel: 'Name',
        manufacturerLabel: 'Manufacturer',
        priceLabel: 'Price ({currency})',
        imagesLabel: 'Files',
        linkedHistory: 'Linked History',
        partManagedNote: 'This part is managed through an expense',
        cannotDeleteNote: 'Since this part is linked to an expense, it cannot be deleted separately. Delete or edit the expense to manage this part\'s lifecycle.',
        partNamePlaceholder: 'Part name...',
        manufacturerPlaceholder: 'Bosch, Brembo, etc.',
        additionalInfoPlaceholder: 'Additional information...',
        namePlaceholder: 'e.g. Brake Pads',
        codePlaceholder: 'e.g. 5Q0 698 151',
        linkPlaceholder: 'https://...',
        vinPlaceholder: 'WBADT...',
        savePart: 'Save Part',
        deletePart: 'Delete spare part',
        deleteConfirm: 'Are you sure you want to delete this spare part?',
        sparePartsLabel: 'Spare Parts',
        partNumber: 'Part #{number}',
        imagesLimitLabel: 'Files (up to 5)',
        optional: '',
        // V2 keys
        selectPart: 'Select Spare Part',
        createNew: 'Create new spare part',
        searchPlaceholder: 'Search by name or code...',
        noResults: 'No parts found',
        noPartsYet: 'No parts yet. Create your first one!',
        selectorDescription: 'Find and select an existing spare part, or create a new one',
        alreadyAdded: 'This part is already added',
        statistics: {
            avgPrice: 'Avg',
            lastPrice: 'Last',
            usedTimes: 'Used {count} times',
            totalPurchased: 'Total: {quantity}x'
        },
        usage: {
            title: 'This Purchase',
            quantity: 'Quantity',
            pricePerUnit: 'Price per unit',
            comment: 'Comment',
            commentPlaceholder: 'e.g., Bought at AutoZone',
            total: 'Total',
            transactionData: 'This Transaction'
        },
        masterData: 'Main Part Info',
        masterRecord: {
            title: 'Part Details (shared)',
            editMaster: 'Edit Master Record',
            warning: 'Changes to master record affect all usages'
        }
    },
    reminders: {
        title: 'Reminders',
        reminder: 'Reminder',
        noteReminder: 'Note Reminder',
        partReminder: 'Part Reminder',
        today: 'Today',
        tomorrow: 'Tomorrow',
        yesterday: 'Yesterday',
        inXDays: 'in {x} days',
        xDaysAgo: '{x} days ago',
        inXMonths: 'in {x} months',
        xMonthsAgo: '{x} months ago',
        active: 'Active',
        activeDesc: 'If necessary, you can turn off this reminder without deleting it',
        disabled: 'Disabled',
        yearly: 'Yearly',
        markSeen: 'Acknowledge',
        addReminder: 'Create New',
        editReminder: 'Edit Reminder',
        extraReminders: 'Extra Reminders',
        extraRemindersDesc: 'Send 3 more reminders on following days (4 total) if not stopped.',
        remindDate: 'Date',
        remindTime: 'Time',
        repeatYearly: 'Repeat Yearly',
        repeatYearlyDesc: 'This reminder will automatically occur every year on the same date/month/day/time until disabled',
        standalone: 'Standalone Reminders',
        embedded: 'Linked Reminders',
        noReminders: 'No active reminders',
        deleteReminder: 'Delete Reminder',
        nameLabel: 'Name',
        descriptionLabel: 'Description',
        namePlaceholder: 'Reminder name',
        descriptionPlaceholder: 'Short description or note',
        simulateReminder: 'Simulate reminder moment',
        simulateSuccess: 'Simulation email sent',
        timezone: 'Timezone',
        nextReminder: 'Next reminder',
        futureTimeError: 'Reminder time must be in the future',
    },
    notes: {
        title: 'Notes',
        emptyNote: 'Empty note',
        noNotes: 'No notes yet',
        addNote: 'Add Note',
        editNote: 'Edit Note',
        viewNote: 'View Note',
        openNote: 'Open Note entry',
        titleLabel: 'Title',
        contentLabel: 'Content',
        noteTitlePlaceholder: 'Note title...',
        noteContentPlaceholder: 'Write your note here...',
        saveNote: 'Save Note',
        deleteNote: 'Delete Note',
        publicNote: 'Make note public',
        publicNoteDesc: 'Others can view this note via shared link',
        deleteConfirm: 'Are you sure you want to delete this note?',
        enterTitleAlert: 'Please enter a title',
        saveFailed: 'Failed to save note',
        thisNote: 'This note',
        deleteFailed: 'Failed to delete note',
        pinToTop: 'Pin to top',
    },
    locations: {
        title: "Locations",
        singleTitle: "Location",
        newLocation: "New Location",
        emptyLocation: "Empty location",
        noLocations: "For statistics - you can add the location where this car expense occurred",
        addLocation: "Add Location",
        editLocation: "Edit Location",
        viewLocation: "View Location",
        openLocation: "Open Location",
        createNew: "Create New",
        selectLocation: "Select location",
        searchLocations: "Search locations...",
        nameLabel: "Name",
        addressLabel: "Address or Coordinates",
        descriptionLabel: "Comment/Description",
        namePlaceholder: "e.g., Downtown Service",
        mapLabel: "Location",
        searchPlaceholder: "Search address...",
        addressPlaceholder: "Enter address and select...",
        descriptionPlaceholder: "Additional info...",
        fillAddress: "Fill address from map",
        saveLocation: "Save Location",
        deleteLocation: "Delete location",
        deleteConfirm: "Are you sure you want to delete this location?",
        enterNameAlert: "Please enter a name",
        saveFailed: "Failed to save location",
        thisLocation: "This location",
        deleteFailed: "Failed to delete location",
        myLocations: "My Locations",
        usedInEntries: "Used in entries",
        noEntries: "Location is not used",
        noEntriesThisCar: "This location has not been used for this car",
        removeLocation: "Remove location",
        noResults: "No results found",
    },
    gallery: {
        title: 'Files',
        noImages: 'No images added',
        addImagesNote: 'Add images to car or expenses',
        totalImages: 'Total {count} images',
        imageAlt: 'Image {number}',
        commentPlaceholder: 'File comment',
        isReceipt: 'This file is a receipt',
        deleteImageTitle: 'Delete file',
        deleteImageConfirm: 'Are you sure you want to permanently delete this file?',
    },
    search: {
        placeholder: 'Search expenses, parts, notes...',
        typeToSearch: 'Type to start searching',
        noResults: 'No results found for "{query}"',
        carImage: 'Car Image',
        expenseImage: 'Expense Image',
        noteImage: 'Note Image',
        galleryImage: 'Gallery Image',
        untitledExpense: 'Untitled Expense',
    },
    landing: {
        nav: {
            features: 'Features',
            howItWorks: 'How it Works',
            pricing: 'Pricing',
            login: 'Log in',
            getStarted: 'Get Started',
            garage: 'Garage',
        },
        hero: {
            title: 'Stop Guessing. Start Knowing the',
            subtitle: 'True Value of Your Car.',
            description: 'Treat your car like the investment it is: Turn your service history into a strategic advantage and know the exact moment to keep, sell, or upgrade.',
            subheadline: 'The professional data-driven dashboard for meticulous car owners.',
            startTracking: 'Get Started',
            viewDemo: 'Demo Car ->',
            trustSignal: 'Join 1,200+ meticulous owners who have already mapped over 5 million kilometers of real-world car costs.',
            ctaNote: 'No credit card. No pushy sales. Just clarity.',
            benefits: {
                repairVsSell: {
                    title: 'Spot the "Repair vs. Sell" threshold',
                    description: 'Stop wondering if you’re dumping cash into a money pit. Know the exact moment a repair is no longer a smart investment.'
                },
                costPerKm: {
                    title: 'Track your real cost-per-kilometer',
                    description: 'Go beyond the dashboard. See the full financial picture of your driving habits that other apps leave out.'
                },
                automation: {
                    title: 'Automate your record-keeping',
                    description: 'Just forward your invoices or simply email your notes. Our AI organizes your car’s technical history for you.'
                }
            }
        },
        features: {
            title: 'The Engine',
            subtitle: 'Powerful tools to help you manage your vehicle with precision.',
            expenseTracking: {
                title: "Expense Tracking",
                description: "Log fuel, service, and insurance to visualize spending trends."
            },
            spareParts: {
                title: "Spare Parts Manager",
                description: "Keep a detailed inventory of part codes and compatibility notes."
            },
            serviceHistory: {
                title: "Service History & Notes",
                description: "A digital service book for modifications and future plans."
            },
            documentStorage: {
                title: "Document Storage",
                description: "Keep receipts, invoices, and photos safe and accessible."
            },
            emailForwarding: {
                title: "Email Forwarding",
                description: "Forward digital invoices directly to your unique garage inbox."
            },
            multiCar: {
                title: "Multi-Car Garage",
                description: "Manage one car or a whole fleet with aggregated statistics."
            },
            repeatingExpenses: {
                title: "Repeating Expenses",
                description: "Automate tracking for monthly costs like leasing."
            },
            smartReminders: {
                title: "Smart Reminders",
                description: "Never miss a maintenance task with custom alerts."
            }
        },
        howItWorks: {
            title: "Simple, powerful workflow",
            step1: {
                title: "Add your vehicle",
                description: "Enter your car details to create your digital garage."
            },
            step2: {
                title: "Log expenses & files",
                description: "Snap a photo of a receipt or forward an email. We handle the rest."
            },
            step3: {
                title: "Get insights",
                description: "See your cost per kilometer, monthly spending, and maintenance timeline."
            }
        },
        useCases: {
            title: "Who this is for",
            enthusiasts: {
                title: "The Meticulous Enthusiast",
                description: "Track every part, mod, and technical spec of your project car."
            },
            dailyDrivers: {
                title: "The Practical Driver",
                description: "Know exactly how your commute affects your budget and when to upgrade."
            },
            flippers: {
                title: "The Smart Flipper",
                description: "Build a verifiable volume of data that proves your car’s true value to buyers."
            },
            fleets: {
                title: "The Small Fleet Owner",
                description: "Manage multiple company vehicles without enterprise software bloat."
            }
        },
        foundersNote: {
            title: "Founder's Note",
            content: "I’m building MyCarCosts to be the ultimate tool for people who actually care about their cars. Is there a feature missing from your workflow? Contact me directly. If it makes sense for the community, we’ll build it together."
        },
        pricing: {
            title: "Simple, transparent pricing",
            subtitle: "Start for free, upgrade as you grow.",
            freeLimitedTime: "Free for limited time",
            perMonth: "month",
            monthly: "Monthly",
            yearly: "Yearly",
            save25: "Save 25%",
            billedAnnually: "Billed annually ({amount})",
            free: {
                title: "Free",
                cta: "Get Started"
            },
            pro: {
                title: "Pro",
                cta: "Upgrade to Pro",
                recommended: "Recommended",
                earlyAdopter: "Early adopter pricing",
                priceLock: "Lock in this price now. Price may increase in the future."
            },
            custom: {
                title: "Custom",
                cta: "Contact Us",
            },
            features: {
                oneCar: "1 car",
                unlimitedExpenses: "Unlimited expenses",
                unlimitedSpareParts: "Unlimited spare parts",
                unlimitedNotes: "Unlimited notes",
                repeatedExpenses: "Repeated expenses",
                emailReminders: "Notes and spare parts email reminders",
                basicStatistics: "Basic statistics",
                fullStatistics: "Full statistics",
                storage50mb: "50 Mb file storage",
                unlimitedArchivedCars: "Unlimited archived cars",
                dataExport: "Data export",
                allInFreePlus: "all in free plus:",
                threeCars: "up to 3 cars",
                storage2gb: "2 Gb file storage",
                emailParsing: "Email parsing",
                carAdmins: "Car admins",
                moreCars: "More cars",
                moreFileStorage: "More file storage",
                customRequirements: "Custom requirements",
            },
            upgradePopup: {
                title: "Coming Soon!",
                message: "The pro and custom versions are not launched yet, but they will be soon. To help us launch faster - please write down which specific feature you expect from the pro / custom version, or anything else you'd like us to improve to make this platform even better for you!",
                placeholder: "Write your feedback here...",
                submit: "Send",
                cancel: "Cancel",
                thanksTitle: "Thank you!",
                thanksMessage: "We have received your feedback and will work to make MyCarCosts even better!",
                thanksOk: "Ok",
            },
        },
        migration: {
            title: "Moving from Excel or another app?",
            subtitle: "We offer a professional data migration service to get you up and running instantly.",
            pricingModel: "Pricing model",
            small: {
                title: "Small dataset",
                description: "< 500 rows",
                price: "$19–29"
            },
            medium: {
                title: "Medium dataset",
                description: "500–5,000 rows",
                price: "$49"
            },
            large: {
                title: "Large / Messy",
                description: "Legacy systems or complex files",
                price: "Custom quote"
            },
            process: {
                title: "How it works",
                step1: "You submit your file",
                step2: "We evaluate the data",
                step3: "One-time payment",
                step4: "We handle the import"
            },
            cta: "Request Migration"
        },
        repairAnalysisSection: {
            title: "Repair Decision Analysis",
            subtitle: "Professional car health and financial assessment. Stop guessing and start knowing if those repairs are truly worth it for your specific vehicle.",
            features: {
                personalized: {
                    title: "Personalized Analysis",
                    description: "For your tracked cars, we use real historical expenses and parameters, adding planned costs to generate an assessment."
                },
                public: {
                    title: "Public Simulator",
                    description: "Evaluating a potential purchase? Use our public tool at /repair to generate a detailed analysis by entering just a few key details."
                },
                whatIf: {
                    title: "What-If Simulator",
                    description: "Input any planned repair cost and instantly see how it affects your car's overall financial health score."
                }
            },
            cta: "Repair Decision Analysis"
        },
        faq: {
            title: "Frequently Asked Questions",
            subtitle: "Everything you need to know about My Car Costs",
            questions: {
                mobileApps: {
                    question: "Will there be iOS and Android apps?",
                    answer: "Yes! Mobile apps for both iOS and Android are coming soon. We're working hard to bring you the best mobile experience."
                },
                dataPrivacy: {
                    question: "Is my data private and secure?",
                    answer: "Absolutely. Your data is encrypted and stored securely. Only you can access your car information unless you explicitly choose to make it public."
                },
                freePlan: {
                    question: "Is the free plan really free forever?",
                    answer: "Yes! The free plan will always be available with one car and all essential features. You can upgrade to Pro anytime for up to 3 cars and advanced features."
                },
                dataExport: {
                    question: "Can I export my data?",
                    answer: "Yes, you can export all your data at any time in CSV or PDF format. Your data is yours, and you're never locked in."
                },
                emailImport: {
                    question: "How does email import work?",
                    answer: "Each car gets a unique email address. Just forward your invoices and we'll extract the data automatically."
                },
                multipleUsers: {
                    question: "Can multiple people access the same car?",
                    answer: "Currently, each car belongs to one account. However, you can make a car publicly viewable to share read-only access with others."
                }
            }
        },
        cta: {
            title: 'Master Your Car\'s Costs Today.',
            subtitle: 'Get the Data. Stop the Guessing.',
            button: 'Get Started',
            note: 'No credit card. No pushy sales. Just clarity.'
        },
        footer: {
            product: "Product",
            legal: "Legal",
            changelog: "Changelog",
            privacy: "Privacy Policy",
            contact: "Contact",
            contactEmail: "info@mycarcosts.com",
            questions: "Any questions? Contact us at:"
        },
        privacy: {
            title: "Privacy Policy",
            lastUpdated: "Last updated: March 24, 2026",
            introduction: "At My Car Costs, we take your privacy seriously. This policy explains how we handle your data in accordance with modern privacy standards, including GDPR.",
            dataCollection: {
                title: "Data Collection",
                description: "We only collect the absolute minimum information required to provide our service.",
                email: "Account Identity: We use your email address as your unique identifier and for authentication (e.g., magic links or Google login).",
                documents: "Document Storage: Files you upload are stored securely in encrypted cloud storage."
            },
            carData: {
                title: "Your Data Control",
                description: "Information you enter (expenses, mileage, parts) is stored for your personal use and analytics.",
                noValidation: "We do not validate the car data you provide; it is your responsibility to ensure accuracy.",
                privateByDefault: "By default, your data is private. Technical identifiers for files are non-sequential and not indexed by search engines.",
                rightToForgotten: "Right to be Forgotten: You can delete your account at any time, which immediately and permanently removes all your records and files."
            },
            thirdParty: {
                title: "Third-party Services",
                description: "We do not sell your personal data. When using Google Login, we only access your email address for identification purposes."
            },
            emailIngestion: {
                title: "Email Ingestion",
                description: "Emails sent to our ingestion service are stored to provide a processing history. You can manually delete individual emails or your entire history at any time."
            }
        },
        emails: {
            deleteAll: "Delete All",
            deleteAllConfirm: "Are you sure you want to delete all email history for this car? This action is irreversible.",
            deleteConfirm: "Are you sure you want to delete this email?",
            noEmails: "No emails received yet."
        },
        terms: {
            title: "Terms and Conditions",
            lastUpdated: "Last updated: March 25, 2026",
            introduction: "By accessing or using MyCarCosts, you agree to be bound by these Terms and Conditions. If you do not agree, please do not use our service.",
            acceptance: {
                title: "1. Acceptance of Terms",
                description: "These terms constitute a legally binding agreement between you and MyCarCosts regarding your use of our application and services."
            },
            service: {
                title: "2. Description of Service",
                description: "MyCarCosts provides a platform for vehicle expense tracking, maintenance management, and financial analysis. This includes AI-powered receipt parsing and data visualization tools."
            },
            responsibilities: {
                title: "3. User Responsibilities",
                description: "You are responsible for the accuracy of any data you input, including mileage, VIN numbers, and costs. You must keep your login credentials (email/magic link) secure and not share them with unauthorized parties."
            },
            plans: {
                title: "4. Service Plans and Limits",
                description: "We offer Free and Pro plans with different storage, car count, and upload limits. We reserve the right to modify these limits or introduce new subscription tiers at any time."
            },
            disclaimer: {
                title: "5. Disclaimers and Accuracy",
                description: "MyCarCosts is an information and organization tool. We do not provide official vehicle health certifications. AI-parsed data from receipts should always be verified by the user for accuracy."
            },
            liability: {
                title: "6. Limitation of Liability",
                description: "MyCarCosts is provided 'as-is'. We are not liable for any mechanical issues, financial losses, or data inaccuracies resulting from the use of our application."
            },
            termination: {
                title: "7. Termination",
                description: "You may stop using the service and delete your data at any time. We reserve the right to suspend or terminate accounts that violate these terms or engage in abusive behavior."
            }
        }
    },
    audit: {
        hero: {
            badge: 'New: Protection Tool',
            title: 'Consult the Oracle.',
            subtitle: 'Stop second-guessing your mechanic. Paste your quote, describe the noise, or upload a photo. Get a direct, logic-based verdict in 60 seconds.',
            cta: 'Consult the Oracle',
            smallPrint: '100% Private. No credit card required.',
            walletSecurity: 'AI-Powered Security for Your Wallet'
        },
        demo: {
            title: 'Audit Results',
            verdictSell: 'Verdict: SELL',
            repairVsValue: 'Repair vs Value',
            cashGapSavings: 'Cash Gap to Replace',
            savingsValue: '$3,200 Savings',
            roiStrategyTitle: 'ROI Strategy',
            roiStrategyText: 'Investing $2,100 into a deteriorating $4,500 asset is a negative ROI. Replacing with a 2018 model now saves you $3,200 in long-term equity.',
            benefitsTitle: 'Stop Guessing. Master Your Car\'s ROI.',
            ctaSubtext: 'Stop guessing. Put your car\'s costs into logic-driven perspective.'
        },
        form: {
            uploadTitle: 'Photos or Documents (Optional)',
            uploadDesc: 'Upload a photo of your quote or just a picture of the problem area.',
            emailLabel: 'Email Address',
            emailPlaceholder: 'Where to send your report?',
            carLabel: 'Car Model',
            carPlaceholder: 'e.g. 2018 BMW X5 TDI 2.0 sedan',
            descLabel: 'Describe the problem',
            descPlaceholder: 'Paste your mechanic quote, describe the noise, or tell us the problem...',
            submit: 'Consult the Oracle',
            submitting: 'Processing...',
            successTitle: 'Quote Received!',
            successDesc: 'Our AI agents are analyzing your data. You will receive a detailed report at {email} within 60 seconds.',
            errorNoFiles: 'Please provide a description or upload a file.',
            errorNoEmail: 'Please enter your email address.',
            verifiedBy: 'Verified by Double-Agent Protocol'
        },
        progress: {
            analyzing: 'Analysis in progress...',
            scanning: 'Scanning Documents',
            checking: 'Checking Market Data',
            roi: 'ROI Calculations',
            finalizing: 'Finalizing Report',
            lightningTitle: 'First Look Verdict',
            lightningBadge: 'LIGHTNING ANALYSIS',
            firstImpression: 'First Impression',
            checkEmail: 'Check your email in 60 seconds',
            anotherAudit: 'Audit Another Situation',
            statusDesc: 'Our Double-Agent system is scrutinizing your data. The final report will be sent to your email.'
        },
        valueProps: {
            negotiation: {
                title: 'ROI Analysis',
                desc: 'Stop overpaying for labor. Get data-backed arguments for real repair times.'
            },
            peace: {
                title: 'Verdict in 60s',
                desc: 'Know for sure if your mechanic is honest about repairs or trying to "bloat" the bill.'
            },
            scams: {
                title: 'Avoid Common Scams',
                desc: 'Our database found that the average quote in big city dealerships is padded by 22% for "consultation" fees that aren\'t necessary.'
            },
            technical: {
                title: 'Replacement Logic',
                desc: 'We calculate the "Cash Gap" — the true cost of fixing your car versus replacing it with something reliable.'
            }
        },
        cta: {
            title: 'Stop dumping cash into a car that isn’t worth it.',
            button: 'Audit the Situation Now'
        },
        howItWorks: {
            title: 'How It Works',
            subtitle: 'The "Double-Agent" verification system ensures you get facts, not fluff.',
            extract: {
                title: 'Data Intake',
                desc: 'We analyze your situation and car model, factoring in mileage and historical maintenance trends.'
            },
            analyze: {
                title: 'Market Analysis',
                desc: 'Our AI compares repair costs against current market valuations and model reliability scores.'
            },
            audit: {
                title: 'ROI Calculation',
                desc: 'We calculate the financial gap between fixing your current vehicle and moving to a better one.'
            },
            verdict: {
                title: 'The Verdict',
                desc: 'You receive a clear "Repair or Sell" report with logic-driven strategy and priority action items.'
            }
        },
        results: {
            priceRealityCheck: 'Price Reality Check',
            roadmapTitle: 'What breaks next?',
            roadmapBadge: 'Roadmap Teaser',
            unlockFullReport: 'Unlock Full Forensic Audit',
            realWorldInsights: 'Real-World Insights',
            oracleAdvice: 'Real Advice from the Oracle',
            oracleAdviceSub: 'See how our AI helps real people navigate complex car problems every day.',
            readFullAudit: 'Read Full Audit',
            potentialSaving: 'Potential Saving',
            getYourOwn: 'Get your own Oracle Audit',
            savingsPotentialFound: 'Savings Potential Found',
            negotiationScript: 'Negotiation Script'
        },
        caseStudy: {
            backToAudit: 'Back to Audit',
            oracleCaseStudy: 'The Oracle Case Study',
            realUserQuestion: 'The Real User Question',
            officialAudit: 'Official Oracle Audit',
            fullReport: 'Full Resolution Report',
            economicImpact: 'Economic Impact',
            startYourAudit: 'Start Your Own Audit',
            moreAudits: 'More Real-World Audits',
            sharingIsCaring: "Sharing is caring. Save someone else's wallet.",
            poweredBy: 'Powered by MyCarCosts Double-Agent Intelligence'
        }
    },
    jarvis: {
        title: 'Jarvis',
        subtitle: 'AI Garage Agent',
        askJarvis: 'Ask Jarvis...',
        typing: 'Jarvis is thinking...',
        safetyWarning: 'Safety Warning',
        repairVerdict: 'Repair Verdict',
        diyFeasibility: 'DIY Feasibility'
    },
    repairMode: {
        oracleStatus: 'Oracle Status: {status}',
        verdictKeep: 'KEEP',
        verdictSell: 'SELL',
        verdictBorderline: 'BORDERLINE'
    }
};

export type Translations = typeof en;
